/**
 * ProposalReader — Bailey's proposal page, laid out the way the plan is.
 *
 * Bailey writes each proposal as a short spec under `## ` sections (Problem,
 * Done when, Out of scope, Open questions, sometimes Why now or Rough shape;
 * see `desks/proposalLane/prompt.ts`). Printed as one markdown block, those
 * headings read as body lines and the page was a wall of text beside Reeve's
 * plan. So this reader lays out the SAME markdown, display only, in the plan
 * reader's own grammar:
 *
 *   1. The Problem is the opening: it prints first, without a heading, the
 *      way the plan's Summary does.
 *   2. Every other section folds under its own heading, in the order the page
 *      wrote it. Done when reads as a checklist; Out of scope starts folded
 *      under its count; everything else, Open questions included, starts open.
 *
 * Nothing is dropped: lines before the first heading print above the Problem,
 * and a section the reader does not know prints open under its own heading.
 * A page with no `## ` sections at all is not this reader's to shape; the
 * caller prints it as markdown (`hasProposalSections`).
 */
import { useMemo } from "react";
import { Markdown } from "../components/Markdown.js";
import { PlanSection } from "./PlanReader.js";
import { splitSections } from "./planMarkdown.js";

/** What the person deciding can skip on a first read. Folded by default. */
const FOLDED_SECTIONS = new Set(["out of scope"]);

const LEAD_SECTION = "problem";

/** Whether the page is written in sections this reader can lay out. */
export function hasProposalSections(markdown: string): boolean {
  return splitSections(markdown).some((section) => section.title !== "");
}

export function ProposalReader({ markdown }: { markdown: string }) {
  const sections = useMemo(() => splitSections(markdown), [markdown]);
  const preamble = sections[0]?.lines.join("\n").trim() ?? "";
  const titled = sections.filter((section) => section.title !== "");
  const lead = titled.find((section) => section.title.toLowerCase() === LEAD_SECTION);
  const leadText = lead?.lines.join("\n").trim() ?? "";
  const shown = titled.filter((section) => section !== lead);
  return (
    <div className="wk-planreader wk-proposalreader" data-testid="proposal-reader">
      {preamble && <Markdown className="wk-plansec-body" text={preamble} />}
      {leadText && (
        <div className="wk-proposal-lead" data-testid="proposal-lead">
          <Markdown className="wk-plansec-body" text={leadText} />
        </div>
      )}
      {shown.map((section, index) => (
        <PlanSection
          key={`${index}:${section.title}`}
          section={section}
          initialOpen={!FOLDED_SECTIONS.has(section.title.toLowerCase())}
        />
      ))}
    </div>
  );
}
